"use client";

import { useEffect } from "react";

export interface DemoSpinItem {
  id: number;
  name: string;
  price: number;
  img: string;
  pct: number;
  color: string;
}

/* ── Sound engine — the real cs2skin audio ──
   round_active.mp3 (~4.15s) plays over the whole spin — its baked-in
   ticking IS the roll sound. round_result.mp3 fires the moment the
   strip lands. UI clicks stay as a tiny synthesized blip. */
const ROUND_ACTIVE_SRC = "/assets/music/round_active.mp3";
const ROUND_RESULT_SRC = "/assets/music/round_result.mp3";

function createSoundEngine() {
  const active = new Audio(ROUND_ACTIVE_SRC);
  active.preload = "auto";
  const result = new Audio(ROUND_RESULT_SRC);
  result.preload = "auto";
  const stopActive = () => {
    try {
      active.pause();
      active.currentTime = 0;
    } catch {
      /* noop */
    }
  };
  const spinStart = () => {
    try {
      stopActive();
      active.volume = 0.6;
      void active.play().catch(() => {});
    } catch {
      /* noop */
    }
  };
  const spinEnd = () => {
    try {
      stopActive();
      result.currentTime = 0;
      result.volume = 0.7;
      void result.play().catch(() => {});
    } catch {
      /* noop */
    }
  };
  return { spinStart, spinEnd };
}

/**
 * Progressive enhancement for the captured case-detail page:
 * count picker (1-6) updates the open price, Fast Spin toggles,
 * Demo Spin runs a real weighted spin ported from the live bundle
 * (idle blur marquee -> 3680ms glide + 320ms settle, center cell
 * scaled with a rarity glow blob -> round_result.mp3 + win modal),
 * and Open prompts login (logged-out snapshot has it disabled).
 */
export function CaseDetailClient({
  price,
  items,
}: {
  price: number;
  items: DemoSpinItem[];
}) {
  useEffect(() => {
    const root = document.getElementById("verbatim-root");
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const sounds = createSoundEngine();
    const on = (
      el: Element | Document | null,
      ev: string,
      fn: EventListener,
      opts?: AddEventListenerOptions
    ) => {
      if (!el) return;
      el.addEventListener(ev, fn, opts);
      cleanups.push(() => el.removeEventListener(ev, fn, opts));
    };

    const fmt = (n: number) =>
      n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>(
        ".ControlsBar_caseButton__CIj63"
      )
    );
    const openBtn = root.querySelector<HTMLButtonElement>(
      ".ControlsBar_openButton__rt5yT"
    );
    const demoBtn = root.querySelector<HTMLButtonElement>(
      ".ControlsBar_demoButton__YSp7c button"
    );
    const fastBtn = root.querySelector<HTMLButtonElement>(
      'button[aria-label="Toggle Fast Spin"]'
    );

    let count = 1;
    let fast = fastBtn?.getAttribute("aria-pressed") === "true";

    const setCount = (n: number) => {
      count = n;

      buttons.forEach((b) => {
        const active = b.textContent?.trim() === String(n);
        b.classList.toggle("ControlsBar_active__DEJyE", active);
        b.classList.toggle("ControlsBar_inactive__sLXBF", !active);
      });
      if (openBtn) {
        openBtn.childNodes.forEach((node) => {
          if (
            node.nodeType === Node.TEXT_NODE &&
            /Open \d+ for/.test(node.textContent ?? "")
          ) {
            node.textContent = `Open ${n} for `;
          }
        });
        const priceSpan = openBtn.querySelector("span span");
        if (priceSpan) priceSpan.textContent = fmt(price * n);
      }
    };

    buttons.forEach((b) => {
      const fn = () => setCount(Number(b.textContent?.trim() || "1"));
      on(b, "click", fn as EventListener);
    });

    on(
      fastBtn,
      "click",
      (() => {
        fast = !fast;

        if (!fastBtn) return;
        fastBtn.setAttribute("aria-pressed", String(fast));
        fastBtn.classList.toggle("bg-[#7d2efa]", fast);
        fastBtn.classList.toggle("bg-gray-600", !fast);
        const knob = fastBtn.firstElementChild;
        if (knob) {
          knob.classList.toggle("translate-x-[18px]", fast);
          knob.classList.toggle("translate-x-0.5", !fast);
        }
      }) as EventListener
    );

    /* ── Open (logged out) → nudge the header Login like the live site ── */
    if (openBtn) {
      openBtn.removeAttribute("disabled");
      openBtn.style.cursor = "pointer";
      on(
        openBtn,
        "click",
        (() => {
          const headerLogin = Array.from(
            root.querySelectorAll("button")
          ).find(
            (b) =>
              (b.textContent || "").replace(/\s+/g, " ").trim().toLowerCase() ===
              "login"
          );
          headerLogin?.click();
        }) as EventListener
      );
    }

    /* ── Demo spin ─────────────────────────────────────────── */
    const pickWeighted = (pool: DemoSpinItem[]): DemoSpinItem => {
      const total = pool.reduce((s, it) => s + Math.max(0, it.pct), 0);
      let r = Math.random() * (total > 0 ? total : 1);
      for (const it of pool) {
        r -= Math.max(0, it.pct);
        if (r <= 0) return it;
      }
      return pool[pool.length - 1];
    };

    const closeWin = () => {
      document.querySelectorAll(".verbatim-win-overlay").forEach((n) => n.remove());
    };
    const closeResult = () => {
      document.querySelectorAll(".verbatim-result").forEach((n) => n.remove());
    };

    const hexToRgbTriple = (hex: string): string => {
      const h = hex.replace("#", "");
      const full =
        h.length === 3
          ? h
              .split("")
              .map((c) => c + c)
              .join("")
          : h;
      const num = parseInt(full.slice(0, 6), 16);
      if (Number.isNaN(num)) return "136, 71, 255";
      return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
    };

    /* Split "Weapon | Skin (Wear)" for the two-line reveal caption */
    const splitItemName = (name: string) => {
      const pipe = name.indexOf("|");
      if (pipe === -1) return { weapon: name, skin: "" };
      return {
        weapon: name.slice(0, pipe).trim(),
        skin: name.slice(pipe + 1).trim(),
      };
    };

    /* Result reveal: fills the roulette frame with the live site's
       pull panel — purple wash, weapon / skin / price hierarchy. */
    const showResult = (item: DemoSpinItem) => {
      closeResult();
      const { weapon, skin } = splitItemName(item.name);
      const panel = document.createElement("div");
      panel.className = "verbatim-result";
      panel.setAttribute("role", "status");
      panel.innerHTML =
        `<div class="verbatim-result-inner">` +
        `<img class="verbatim-result-img" alt="" src="${item.img}" draggable="false" />` +
        (weapon
          ? `<div class="verbatim-result-weapon"></div>`
          : "") +
        (skin
          ? `<div class="verbatim-result-skin"></div>`
          : "") +
        `<div class="verbatim-result-price">$${fmt(item.price)}</div>` +
        `</div>`;
      const wEl = panel.querySelector(".verbatim-result-weapon");
      if (wEl) wEl.textContent = weapon;
      const sEl = panel.querySelector(".verbatim-result-skin");
      if (sEl) sEl.textContent = skin;
      on(
        panel,
        "click",
        (() => {
          closeResult();
        }) as EventListener
      );
      /* the panel fills the roulette frame itself (strip → viewport → frame) */
      const strip = findStrip();
      const frame =
        (strip?.parentElement?.parentElement as HTMLElement | null) || root;
      if (getComputedStyle(frame).position === "static") {
        frame.style.position = "relative";
      }
      frame.appendChild(panel);
    };

    const showWin = (item: DemoSpinItem) => {
      closeWin();
      const rgb = hexToRgbTriple(item.color);
      const overlay = document.createElement("div");
      overlay.className = "verbatim-win-overlay";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.innerHTML =
        `<div class="verbatim-win-modal">` +
        `<div class="verbatim-win-glow" style="background: radial-gradient(circle, rgba(${rgb}, 0.35) 0%, rgba(${rgb}, 0) 70%);"></div>` +
        `<div class="verbatim-win-eyebrow">Demo win · ${count} case${count > 1 ? "s" : ""}</div>` +
        `<div class="verbatim-win-imgwrap" style="border-color: rgba(${rgb}, 0.5); box-shadow: 0 0 60px -10px rgba(${rgb}, 0.55);">` +
        `<img class="verbatim-win-img" alt="" src="${item.img}" draggable="false" />` +
        `</div>` +
        `<div class="verbatim-win-name"></div>` +
        `<div class="verbatim-win-sub" style="color: rgb(${rgb});">${item.pct}% · drop chance</div>` +
        `<div class="verbatim-win-price"><img alt="coin" src="/images/coin-2.avif" width="16" height="16" />${fmt(item.price)} coins</div>` +
        `<div class="verbatim-win-actions"><button class="verbatim-win-btn" type="button">Spin again</button>` +
        `<button class="verbatim-win-ghost" type="button">Close</button></div>` +
        `</div>`;
      const nameEl = overlay.querySelector(".verbatim-win-name");
      if (nameEl) nameEl.textContent = item.name;
      document.body.appendChild(overlay);
      const again = overlay.querySelector(
        ".verbatim-win-btn"
      ) as HTMLButtonElement | null;
      const ghost = overlay.querySelector(
        ".verbatim-win-ghost"
      ) as HTMLButtonElement | null;
      on(again, "click", (() => {
        closeWin();
        runDemoSpin();
      }) as EventListener);
      on(ghost, "click", (() => {
        closeWin();
      }) as EventListener);
      on(
        overlay,
        "click",
        ((ev: Event) => {
          if (ev.target === overlay) closeWin();
        }) as EventListener
      );
    };

    let spinning = false;

    const findStrip = (): HTMLElement | null => {
      const direct = root.querySelector(
        'div[style*="idleScrollDynamic"]'
      ) as HTMLElement | null;
      if (direct) return direct;
      const container = root.querySelector(
        "#cases-game-container"
      ) as HTMLElement | null;
      if (container) {
        const cand = Array.from(container.querySelectorAll("div")).find((d) => {
          const s = (d as HTMLElement).getAttribute("style") || "";
          return s.includes("max-content") && s.includes("animation");
        }) as HTMLElement | undefined;
        if (cand) return cand;
      }
      const cands = Array.from(
        root.querySelectorAll('div[class*="gap-5"]')
      ) as HTMLElement[];
      for (const c of cands) {
        if (c.querySelector('div[class*="w-[200px]"]')) return c;
      }
      return null;
    };

    const runDemoSpin = () => {
      if (spinning || items.length === 0) return;
      const winner = pickWeighted(items);
      const strip = findStrip();
      if (!strip) {
        showWin(winner);
        return;
      }
      const firstCell = strip.querySelector(
        'div[class*="w-[200px]"]'
      ) as HTMLElement | null;
      if (!firstCell) {
        showWin(winner);
        return;
      }
      const viewport = (strip.parentElement as HTMLElement | null) || strip;
      const frame = (viewport.parentElement as HTMLElement | null) || viewport;

      spinning = true;
      closeWin();
      closeResult();

      sounds.spinStart();
      if (demoBtn) {
        demoBtn.style.opacity = "0.5";
        demoBtn.style.pointerEvents = "none";
      }

      const cover = viewport.parentElement?.querySelector(
        ".pointer-events-none.absolute.inset-0.z-20"
      ) as HTMLElement | null;
      const coverPrevDisplay = cover?.style.display ?? "";
      if (cover) cover.style.display = "none";

      const STRIP_LEN = 56;
      const WIN_AT = STRIP_LEN - 6;

      const frag = document.createDocumentFragment();
      const cellEls: HTMLElement[] = [];
      for (let i = 0; i < STRIP_LEN; i++) {
        const item = i === WIN_AT ? winner : pickWeighted(items);
        const cell = firstCell.cloneNode(true) as HTMLElement;
        cell.classList.remove(
          "verbatim-spin-cell-winner",
          "verbatim-cell-focus"
        );
        // idle cells are dim + blurred + shrunk — spin cells are full-bleed
        cell.classList.remove("opacity-60", "scale-70");
        cell.classList.add("opacity-100");
        cell.style.opacity = "1";
        // rarity color drives the center-cell glow blob (live: --rc-rgb)
        cell.style.setProperty("--rc-rgb", hexToRgbTriple(item.color));
        const blob = document.createElement("div");
        blob.className = "verbatim-cell-blob";
        cell.appendChild(blob);
        const img = cell.querySelector("img");
        if (img) {
          img.setAttribute("src", item.img);
          img.setAttribute("alt", item.name);
          img.classList.remove("blur-[6px]");
          (img as HTMLElement).style.filter = "none";
          // if a local skin ever 404s, fall back to nothing (no broken icon)
          img.addEventListener(
            "error",
            () => {
              (img as HTMLImageElement).style.visibility = "hidden";
            },
            { once: true }
          );
        }
        frag.appendChild(cell);
        cellEls.push(cell);
      }
      strip.classList.add("verbatim-spin-strip");
      strip.style.animation = "none";
      strip.style.transition = "none";
      strip.innerHTML = "";
      strip.appendChild(frag);

      const probe = cellEls[0];
      const gap = 20;
      const pitch = (probe.getBoundingClientRect().width || 200) + gap;

      const viewW = viewport.getBoundingClientRect().width || 800;
      const target = -(WIN_AT * pitch + pitch / 2 - viewW / 2);

      /* frame dims to 0.9 while rolling (live behavior) */
      frame.classList.add("verbatim-spin-frame", "verbatim-frame-dim");

      /* ── live tick loop: the cell nearest the pointer gets scale(1.15)
            + its rarity glow blob, everything else stays flat ── */
      let raf = 0;
      let focused: HTMLElement | null = null;
      const tick = () => {
        const vrect = viewport.getBoundingClientRect();
        const cx = vrect.left + vrect.width / 2;
        let best: HTMLElement | null = null;
        let bestD = Infinity;
        for (const c of cellEls) {
          const r = c.getBoundingClientRect();
          if (r.right < vrect.left - 60 || r.left > vrect.right + 60) continue;
          const d = Math.abs(r.left + r.width / 2 - cx);
          if (d < bestD) {
            bestD = d;
            best = c;
          }
        }
        if (best !== focused) {
          focused?.classList.remove("verbatim-cell-focus");
          best?.classList.add("verbatim-cell-focus");
          focused = best;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);

      strip.style.transform = "translateX(0px)";
      void strip.offsetWidth;

      /* ── two-phase roll, exact live timings ──
         phase 1: 3680ms cubic-bezier(0.01, 0.98, 0.45, 1) glide to a
         jittered point near the winner (never the exact center — the
         settle phase always has a little correction to do)
         phase 2: 320ms ease-out onto the exact center under the pointer */
      const duration = 4000;
      const glideMs = 3680;
      const jitter = (Math.random() - 0.5) * (pitch * 0.4);
      const glideTarget = target + jitter;

      strip.style.transition = `transform ${glideMs}ms cubic-bezier(0.01, 0.98, 0.45, 1)`;
      requestAnimationFrame(() => {
        strip.style.transform = `translateX(${glideTarget}px)`;
      });

      const settleTimer = window.setTimeout(() => {
        strip.style.transition =
          "transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)";
        strip.style.transform = `translateX(${target}px)`;
      }, glideMs);

      const finish = () => {
        cancelAnimationFrame(raf);
        sounds.spinEnd();
        frame.classList.remove("verbatim-frame-dim");
        const winCell = cellEls[WIN_AT];
        if (winCell) {
          winCell.classList.add("verbatim-spin-cell-winner");
          const img = winCell.querySelector("img");
          if (img) (img as HTMLElement).style.filter = "none";
          const rgb = hexToRgbTriple(winner.color);
          winCell.style.filter = `drop-shadow(0 0 28px rgba(${rgb}, 0.55))`;
        }
        spinning = false;
        if (demoBtn) {
          demoBtn.style.opacity = "";
          demoBtn.style.pointerEvents = "";
        }
        if (cover) cover.style.display = coverPrevDisplay;
        showResult(winner);
      };

      const timer = window.setTimeout(finish, duration + 150);
      cleanups.push(() => {
        window.clearTimeout(timer);
        window.clearTimeout(settleTimer);
        cancelAnimationFrame(raf);
      });
    };

    if (demoBtn) {
      demoBtn.style.cursor = "pointer";
      on(demoBtn, "click", runDemoSpin as EventListener);
    }

    on(
      document,
      "keydown",
      ((ev: Event) => {
        if ((ev as KeyboardEvent).key === "Escape") closeWin();
      }) as EventListener
    );

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch {
          /* noop */
        }
      });
      closeWin();
      closeResult();
    };
  }, [price, items]);

  return null;
}
